import { createClient } from '@libsql/client';
import { nanoid } from 'nanoid';

const db = createClient({
  url: import.meta.env.VITE_TURSO_DB_URL,
  authToken: import.meta.env.VITE_TURSO_AUTH_TOKEN,
});

export const propertyService = {
  async create(properties) {
    const title = properties.title || '';
    const slug = title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

    const description = properties.description || '';
    const price = parseFloat(properties.price);
    const location = properties.location || '';
    const bedrooms = parseInt(properties.bedrooms);
    const bathrooms = parseInt(properties.bathrooms);
    const area = parseFloat(properties.area);
    const status = properties.status || 'available';
    const property_type = properties.property_type || '';
    const featured = 0;

    const images = JSON.stringify(properties.images || []);
    const amenities = properties.amenities || '[]';
    const address = properties.address || '';
    const city = properties.city || '';
    const state = properties.state || '';
    const parking = parseInt(properties.parking) || 0;
    const year_built = parseInt(properties.year_built) || 0;
    const lat = parseFloat(properties.lat); // Added latitude
    const lng = parseFloat(properties.lng); // Added longitude

    await db.execute({
      sql: `INSERT INTO properties (
        slug, title, description, price, location, bedrooms, bathrooms, area,
        status, property_type, featured, images, amenities,
        address, city, state, parking, year_built, lat, lng
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      args: [
        slug,
        title,
        description,
        isNaN(price) ? null : price,
        location,
        isNaN(bedrooms) ? null : bedrooms,
        isNaN(bathrooms) ? null : bathrooms,
        isNaN(area) ? null : area,
        status,
        property_type,
        featured,
        images,
        amenities,
        address,
        city,
        state,
        isNaN(parking) ? null : parking,
        isNaN(year_built) ? null : year_built,
        isNaN(lat) ? null : lat, 
        isNaN(lng) ? null : lng, 
      ],
    });

    const result = await db.execute({
      sql: `SELECT * FROM properties WHERE slug = ?`,
      args: [slug],
    });

    return result.rows[0];
  },

  async update(id, properties) {
    const updates = [];
    const values = [];

    Object.entries(properties).forEach(([key, value]) => {
      if (value !== undefined) {
        if (key === 'images' || key === 'amenities') {
          updates.push(`${key} = ?`);
          values.push(typeof value === 'string' ? value : JSON.stringify(value));
        } else if (key === 'lat' || key === 'lng') { 
          updates.push(`${key} = ?`);
          values.push(isNaN(parseFloat(value)) ? null : parseFloat(value));
        }
        else {
          updates.push(`${key} = ?`);
          values.push(value);
        }
      }
    });

    if (updates.length > 0) {
      updates.push('updated_at = CURRENT_TIMESTAMP');
      await db.execute({
        sql: `UPDATE properties SET ${updates.join(', ')} WHERE id = ?`,
        args: [...values, id],
      });
    }

    return this.getById(id);
  },

  async getById(id) {
    const result = await db.execute({
      sql: 'SELECT * FROM properties WHERE id = ?',
      args: [id],
    });

    if (!result.rows[0]) {
      throw new Error('Property not found');
    }

    const row = result.rows[0];

    return {
      ...row,
      images: JSON.parse(row.images || '[]'),
      amenities: JSON.parse(row.amenities || '[]'),
      price: Number(row.price),
      area: Number(row.area),
      bedrooms: Number(row.bedrooms),
      bathrooms: Number(row.bathrooms),
      parking: Number(row.parking),
      year_built: Number(row.year_built),
      lat: Number(row.lat), 
      lng: Number(row.lng), 
    };
  },

  async getAll(options = {}) {
    const { limit = 10, offset = 0, type, status } = options;
    const conditions = [];
    const args = [];

    if (type) {
      conditions.push('property_type = ?');
      args.push(type);
    }

    if (status) {
      conditions.push('status = ?');
      args.push(status);
    }

    const whereClause = conditions.length ? `WHERE ${conditions.join(' AND ')}` : '';

    const [properties, countResult] = await Promise.all([
      db.execute({
        sql: `SELECT * FROM properties ${whereClause} ORDER BY created_at DESC LIMIT ? OFFSET ?`,
        args: [...args, limit, offset],
      }),
      db.execute({
        sql: `SELECT COUNT(*) as total FROM properties ${whereClause}`,
        args,
      }),
    ]);

    return {
      properties: properties.rows.map(row => ({
        ...row,
        images: JSON.parse(row.images || '[]'),
        amenities: JSON.parse(row.amenities || '[]'),
        price: Number(row.price),
        area: Number(row.area),
        bedrooms: Number(row.bedrooms),
        bathrooms: Number(row.bathrooms),
        parking: Number(row.parking),
        year_built: Number(row.year_built),
        lat: Number(row.lat), 
        lng: Number(row.lng), 
      })),
      total: Number(countResult.rows[0].total),
    };
  },

  async delete(id) {
    await db.execute({
      sql: 'DELETE FROM properties WHERE id = ?',
      args: [id],
    });
  },
};